'use client';

import { useEffect, useState, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';

/**
 * Tracks live visitor count across all pages using Supabase Realtime Presence.
 * Each browser tab joins a shared channel and the count reflects all active presences.
 * Time-based caps:
 *   22:00–01:00 → 1–7
 *   01:00–05:00 → 1–2
 *   05:00–10:00 → 1–4
 *   10:00–22:00 → 1–36
 */

function applyTimeBasedCount(realCount: number): number {
  const hour = new Date().getHours();

  if (hour >= 22 || hour < 1) {
    // 22:00–01:00: cap to 1–7
    return Math.min(Math.max(1, realCount), 7);
  } else if (hour >= 1 && hour < 5) {
    // 01:00–05:00: cap to 1–2
    return Math.min(Math.max(1, realCount), 2);
  } else if (hour >= 5 && hour < 10) {
    // 05:00–10:00: cap to 1–4
    return Math.min(Math.max(1, realCount), 4);
  } else {
    // 10:00–22:00: cap to 1–36
    return Math.min(Math.max(1, realCount), 36);
  }
}

export function useOnlineCount(): number {
  const [count, setCount] = useState<number>(1);
  const channelRef = useRef<ReturnType<ReturnType<typeof createClient>['channel']> | null>(null);

  useEffect(() => {
    const supabase = createClient();

    // Generate a unique key for this browser tab session
    const presenceKey = `visitor_${Math.random().toString(36).slice(2)}_${Date.now()}`;

    const channel = supabase.channel('site_presence');

    channelRef.current = channel;

    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        // Count unique presence keys (each tab = 1 unique key)
        const total = Object.keys(state).length;
        const base = Math.max(1, total);
        setCount(applyTimeBasedCount(base));
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel.track({
            user_id: presenceKey,
            online_at: new Date().toISOString(),
          });
        }
      });

    return () => {
      channel.untrack().then(() => {
        supabase.removeChannel(channel);
      });
    };
  }, []);

  return count;
}
